import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Checkbox as MuiCheckbox, FormControl as MuiFormControl, FormHelperText as MuiFormHelperText, InputLabel as MuiInputLabel, ListItemText as MuiListItemText, MenuItem as MuiMenuItem, Select as MuiSelect, } from '@mui/material';
import { forwardRef, useCallback, } from 'react';
import { layout } from '../../styles';
import { styled } from '../../utils';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { WithEndDecorator } from '../WithEndDecorator';
export const MultiSelect = forwardRef(function MultiSelect(props, ref) {
    const { onChange, options = [], label, helperText, FormControlProps, required, value, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    input, disabled, endDecorator, emptyEndDecorator, renderValue, ...otherProps } = props;
    const handleChange = useCallback((event) => {
        if (onChange === undefined) {
            return;
        }
        const val = event.target.value; // MUI 5 can also pass a string here, if value is auto-filled
        onChange(typeof val === 'string' ? val.split(',') : val);
    }, [onChange]);
    const handleRenderValue = (rawSelected) => {
        const selected = Array.isArray(rawSelected)
            ? rawSelected
            : typeof rawSelected === 'string'
                ? [rawSelected]
                : [];
        return renderValue ? renderValue(selected) : selected.join(', ');
    };
    const hasValue = Array.isArray(value) && value.length > 0;
    return (_jsx(WithEndDecorator, { endDecorator: endDecorator, emptyEndDecorator: emptyEndDecorator, decoratorComponent: _jsxs(MuiFormControl, { sx: {
                width: '100%',
            }, disabled: disabled, ...FormControlProps, children: [!!label && (_jsxs(LabelStyled, { error: props.error, children: [label, required && '\xa0*'] })), _jsx(SelectStyled, { ref: ref, multiple: true, required: required, value: value, onChange: handleChange, input: _jsx(Input, { hasValue: hasValue }), renderValue: handleRenderValue, IconComponent: (options.length < 2 && EmptyComponent) || ArrowIcon, ...otherProps, children: options.map((option, idx) => (_jsxs(MuiMenuItem, { value: option, children: [_jsx(MuiCheckbox, { checked: value.includes(option) }), _jsx(MuiListItemText, { primary: option })] }, idx))) }), !!helperText && (_jsx(MuiFormHelperText, { error: props.error, children: helperText }))] }) }));
});
const SelectStyled = styled(({ className, ...props }) => (_jsx(MuiSelect, { MenuProps: {
        ...props.MenuProps,
        classes: { paper: `${className}-paper`, list: `${className}-list` },
    }, ...props })))(({ theme }) => ({
    '&-paper': {
        boxShadow: `${layout.shadow.s} !important`,
        borderRadius: `${theme.spacing(0.5)} !important`,
    },
    '&-list': {
        '& .MuiListItem-button:hover': {
            backgroundColor: theme.palette.secondary.contrastText,
        },
        '& .Mui-selected, & .Mui-selected:hover': {
            backgroundColor: theme.palette.groupPrimary.lightDove,
        },
    },
}));
const EmptyComponent = () => null;
const ArrowIcon = () => _jsx(IconStyled, { variant: "arrow-down-2", fontSize: "small" });
const IconStyled = styled(Icon)(({ theme }) => ({
    top: 'calc(50% - 4px)',
    fontSize: theme.typography.body2.fontSize,
    color: 'rgba(0, 0, 0, 0.54)',
    right: 0,
    position: 'absolute',
    pointerEvents: 'none',
}));
const LabelStyled = styled(MuiInputLabel)(({ theme }) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    lineHeight: 1,
    paddingRight: theme.spacing(3),
    transform: `translate(0, ${theme.spacing(2.75)}) scale(1)`,
    '&:not(.Mui-error).Mui-focused': {
        color: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
        color: theme.palette.white.dark,
    },
    '&.MuiInputLabel-shrink': {
        maxWidth: '133%',
        transform: 'translate(0, 1.5px) scale(0.75)',
    },
    '&.MuiInputLabel-animated': {
        transition: theme.transitions.create(['color', 'transform', 'max-width'], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeOut,
        }),
    },
}));
